/**
 * Таймер сна: чистая арифметика, без таймеров и аудио.
 *
 * Отсчёт — по настенным часам (момент окончания), а не «минус секунда за тик»: на
 * заблокированном телефоне браузер душит setInterval, и тиковый счётчик уезжал бы на
 * минуты. Плеер спрашивает состояние на каждом timeupdate и раз в секунду — сколько бы
 * тиков ни пропало, ответ верный.
 */

/** Варианты в минутах — как в продуктовом описании. */
export const SLEEP_MINUTES = [15, 30, 45, 60] as const;

/** Затухание в конце: минута — достаточно, чтобы тишина не будила. */
export const DEFAULT_FADE_MS = 60_000;

export type SleepSetting =
  | { mode: "off" }
  /** endsAt — момент остановки по Date.now(); fadeMs — сколько длится затухание до него. */
  | { mode: "timer"; endsAt: number; fadeMs: number; minutes: number }
  /** Остановиться, когда закончится текущая серия, без автоперехода к следующей. */
  | { mode: "episode_end" };

export interface SleepStatus {
  remainingMs: number;
  /** Множитель громкости 0..1: 1 до начала затухания, 0 в момент остановки. */
  volume: number;
  done: boolean;
}

/** Затухание не длиннее четверти таймера: у тестовой минуты — 15 секунд, а не вся минута. */
export function fadeFor(durationMs: number): number {
  return Math.max(0, Math.min(DEFAULT_FADE_MS, Math.floor(durationMs / 4)));
}

/** Включить таймер на N минут от момента now. */
export function startSleepTimer(minutes: number, now: number): SleepSetting {
  const durationMs = Math.max(0, minutes) * 60_000;
  return { mode: "timer", endsAt: now + durationMs, fadeMs: fadeFor(durationMs), minutes };
}

/**
 * Состояние таймера в момент now. Громкость в окне затухания — квадрат доли оставшегося
 * времени: слух логарифмичен, и линейное снижение звучит как «ровно, ровно, обрыв».
 */
export function sleepTimerStatus(endsAt: number, now: number, fadeMs: number = DEFAULT_FADE_MS): SleepStatus {
  if (!Number.isFinite(endsAt) || !Number.isFinite(now)) return { remainingMs: 0, volume: 1, done: true };
  const remainingMs = Math.max(0, endsAt - now);
  if (remainingMs === 0) return { remainingMs: 0, volume: 0, done: true };
  if (fadeMs <= 0 || remainingMs >= fadeMs) return { remainingMs, volume: 1, done: false };
  const share = remainingMs / fadeMs;
  return { remainingMs, volume: share * share, done: false };
}

/**
 * Обратный отсчёт на кнопке: "14:59", "1:00:00". Секунды округляются вверх — пока
 * таймер не сработал, на кнопке не может быть "0:00".
 */
export function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${ss}` : `${minutes}:${ss}`;
}
